/* ════════════════════════════════════════════════════════════════════════════
   MAISON MERCIER — LE JOURNAL, EN MOUVEMENT
   ════════════════════════════════════════════════════════════════════════════
   ① /blogs/<blog>          la SALLE : les couvertures flottent dans un espace 3D (WebGL),
                            le défilement fait avancer la caméra, l'image ondule au survol.
   ② /blogs/<blog>/<article> la COUVERTURE VIVANTE : l'image d'en-tête ondule, se plie au
                            défilement, reçoit un balayage de lumière à l'arrivée.
   ③ les deux               révélations éditoriales, cartes inclinables, étiquette « Lire ».

   ⛔ AMÉLIORATION PROGRESSIVE, JAMAIS UN PRÉREQUIS
      · Le texte reste du HTML : titres, liens, extraits. Google lit la page sans ce fichier.
      · html.mm-j3d-motion et html.mm-j3d-on sont posés AVANT le premier rendu par le script en
        ligne des sections : la mise en page ne saute pas quand ce fichier arrive.
      · Rien n'est caché tant que ce fichier n'a pas tourné : un élément sous la ligne de flottaison
        n'est mis « en attente » qu'ici, au démarrage. Si ce fichier échoue, tout reste visible.
      · Sans WebGL (ou contexte perdu), la classe mm-j3d-on est retirée : retour au Journal d'origine.
   Pas de bibliothèque : WebGL 1, deux programmes, des plans subdivisés. Images de la boutique
   (même origine) : aucune requête tierce.
   ════════════════════════════════════════════════════════════════════════════ */
(() => {
  if (window.__mmJournal3d) return;
  window.__mmJournal3d = true;

  const html = document.documentElement;
  const MOUVEMENT = html.classList.contains('mm-j3d-motion');
  const POINTEUR_FIN = matchMedia('(pointer: fine)').matches;
  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
  const lerp = (a, b, t) => a + (b - a) * t;
  const lisse = (a, b, v) => { const t = clamp((v - a) / (b - a), 0, 1); return t * t * (3 - 2 * t); };
  // amortissement indépendant de la cadence d'images : 60 ou 120 Hz donnent le même mouvement
  const amorti = (k, dt) => 1 - Math.exp(-k * dt);
  const easeOutExpo = (t) => (t >= 1 ? 1 : 1 - Math.pow(2, -10 * t));
  if (!MOUVEMENT) return;

  /* ── MATRICES (colonne par colonne, comme WebGL) ─────────────────────────── */
  const M = {
    perspective(fov, ratio, proche, loin) {
      const f = 1 / Math.tan(fov / 2), m = new Float32Array(16);
      m[0] = f / ratio; m[5] = f; m[10] = (loin + proche) / (proche - loin); m[11] = -1; m[14] = (2 * loin * proche) / (proche - loin);
      return m;
    },
    // caméra qui regarde un point, axe vertical (0, 1, 0)
    regarde(o, c) {
      let zx = o[0] - c[0], zy = o[1] - c[1], zz = o[2] - c[2];
      let l = Math.hypot(zx, zy, zz); zx /= l; zy /= l; zz /= l;
      let xx = zz, xz = -zx; l = Math.hypot(xx, xz); xx /= l; xz /= l;
      const yx = zy * xz, yy = zz * xx - zx * xz, yz = -zy * xx;
      return new Float32Array([xx, yx, zx, 0, 0, yy, zy, 0, xz, yz, zz, 0,
        -(xx * o[0] + xz * o[2]), -(yx * o[0] + yy * o[1] + yz * o[2]), -(zx * o[0] + zy * o[1] + zz * o[2]), 1]);
    },
    // translation · rotation Y · rotation X · échelle (x, y)
    modele(x, y, z, ry, rx, ex, ey) {
      const cy = Math.cos(ry), sy = Math.sin(ry), cx = Math.cos(rx), sx = Math.sin(rx);
      return new Float32Array([cy * ex, 0, -sy * ex, 0, sy * sx * ey, cx * ey, cy * sx * ey, 0, sy * cx, -sx, cy * cx, 0, x, y, z, 1]);
    },
    mul(a, b) {
      const r = new Float32Array(16);
      for (let c = 0; c < 4; c++) for (let l = 0; l < 4; l++) {
        r[c * 4 + l] = a[l] * b[c * 4] + a[4 + l] * b[c * 4 + 1] + a[8 + l] * b[c * 4 + 2] + a[12 + l] * b[c * 4 + 3];
      }
      return r;
    },
    projeter(m, x, y) { // point local (x, y, 0) → coordonnées normalisées de l'écran
      const cx = m[0] * x + m[4] * y + m[12], cy = m[1] * x + m[5] * y + m[13], cw = m[3] * x + m[7] * y + m[15];
      return [cx / cw, cy / cw, cw];
    },
  };

  /* ── WEBGL : le strict nécessaire ────────────────────────────────────────── */
  // conserver = true : la toile garde son image quand on cesse de dessiner (la couverture se repose).
  // ⛔ Sans lui, mesuré : la couverture au repos s'affichait VIDE, l'image HTML étant déjà masquée.
  function contexte(canvas, conserver = false) {
    const gl = canvas.getContext('webgl', { alpha: true, antialias: true, premultipliedAlpha: true, powerPreference: 'high-performance', preserveDrawingBuffer: conserver });
    if (!gl) return null;
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
    return gl;
  }
  function programme(gl, vs, fs) {
    const s = (type, src) => {
      const o = gl.createShader(type); gl.shaderSource(o, src); gl.compileShader(o);
      if (!gl.getShaderParameter(o, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(o));
      return o;
    };
    const p = gl.createProgram();
    gl.attachShader(p, s(gl.VERTEX_SHADER, vs)); gl.attachShader(p, s(gl.FRAGMENT_SHADER, fs)); gl.linkProgram(p);
    if (!gl.getProgramParameter(p, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(p));
    const u = {}, n = gl.getProgramParameter(p, gl.ACTIVE_UNIFORMS);
    for (let i = 0; i < n; i++) { const nom = gl.getActiveUniform(p, i).name; u[nom] = gl.getUniformLocation(p, nom); }
    return { p, u, a: gl.getAttribLocation(p, 'aPos') };
  }
  // plan subdivisé de -0,5 à 0,5 : assez de sommets pour qu'il se plie sans facettes
  function plan(gl, n) {
    const pos = [], idx = [];
    for (let j = 0; j <= n; j++) for (let i = 0; i <= n; i++) pos.push(i / n - 0.5, j / n - 0.5);
    for (let j = 0; j < n; j++) for (let i = 0; i < n; i++) {
      const a = j * (n + 1) + i, b = a + 1, c = a + n + 1, d = c + 1;
      idx.push(a, b, c, b, d, c);
    }
    const vb = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, vb); gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(pos), gl.STATIC_DRAW);
    const ib = gl.createBuffer(); gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ib); gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint16Array(idx), gl.STATIC_DRAW);
    return { vb, ib, n: idx.length };
  }
  function texture(gl, source) {
    const t = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, t);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    if (source) gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);
    else gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array([245, 241, 233, 255]));
    return t;
  }
  function charger(url) {
    return new Promise((ok, ko) => { const i = new Image(); i.decoding = 'async'; i.onload = () => ok(i); i.onerror = ko; i.src = url; });
  }
  // Le fond d'un packshot (blanc) devient la carte ; une photo pleine page couvre la carte.
  function fondDeLImage(img) {
    try {
      const c = document.createElement('canvas'); c.width = 16; c.height = 16;
      const x = c.getContext('2d'); x.drawImage(img, 0, 0, 16, 16);
      const d = x.getImageData(0, 0, 16, 16).data, coins = [0, 15 * 4, 15 * 16 * 4, (15 * 16 + 15) * 4];
      let r = 0, g = 0, b = 0;
      for (const k of coins) { r += d[k]; g += d[k + 1]; b += d[k + 2]; }
      r /= 1020; g /= 1020; b /= 1020;
      return { fond: [r, g, b], packshot: 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.86 };
    } catch (e) { return { fond: [0.97, 0.96, 0.94], packshot: true }; }
  }
  // Un article sans image reçoit une couverture typographique, dessinée aux polices de la maison.
  function couvertureTexte(titre, date) {
    const c = document.createElement('canvas'); c.width = 720; c.height = 900;
    const x = c.getContext('2d');
    x.fillStyle = '#f5f1e9'; x.fillRect(0, 0, 720, 900);
    x.fillStyle = '#6b283d'; x.font = '500 22px "MM Sans", sans-serif'; x.fillText(`LE JOURNAL — ${date}`, 56, 92);
    x.fillStyle = '#292722'; x.font = '400 76px "MM Bodoni", Georgia, serif';
    const mots = titre.split(' '); let ligne = '', y = 240;
    for (const m of mots) {
      const essai = ligne ? `${ligne} ${m}` : m;
      if (x.measureText(essai).width > 600 && ligne) { x.fillText(ligne, 56, y); ligne = m; y += 86; } else ligne = essai;
    }
    x.fillText(ligne, 56, y);
    x.fillStyle = '#6b283d'; x.beginPath(); x.arc(72, 820, 9, 0, Math.PI * 2); x.fill();
    return c;
  }

  /* ════════════════════════════════════════════════════════════════════════
     ① LA SALLE
     ════════════════════════════════════════════════════════════════════════ */
  const VS_CARTE = `
    attribute vec2 aPos;
    uniform mat4 uProj, uVue, uModele;
    uniform float uCourbe, uTempsV, uGraine, uSurvolV;   // noms propres au sommet : une uniforme partagée doit avoir la même précision des deux côtés
    varying vec2 vUv; varying float vBrume;
    void main() {
      vec2 uv = aPos + 0.5;
      vec3 p = vec3(aPos, 0.0);
      p.z += sin(uv.x * 3.14159) * uCourbe;                           // la vitesse plie la carte
      p.z += sin(uv.y * 5.0 + uTempsV * 0.9 + uGraine) * 0.018;         // elle respire
      p.z += uSurvolV * 0.06 * sin(uv.x * 3.14159) * sin(uv.y * 3.14159); // elle se gonfle au survol
      vec4 v = uVue * uModele * vec4(p, 1.0);
      vBrume = smoothstep(4.0, 17.0, -v.z);
      vUv = vec2(uv.x, 1.0 - uv.y);
      gl_Position = uProj * v;
    }`;
  const FS_CARTE = `
    precision mediump float;
    uniform sampler2D uTex;
    uniform float uPret, uSurvol, uTemps, uDecal, uAlpha, uRatioImg, uRatioCarte, uPackshot, uOmbre, uLumiere;
    uniform vec2 uSouris; uniform vec3 uBrume, uFond;
    varying vec2 vUv; varying float vBrume;
    float coin(vec2 uv, float r) { vec2 q = abs(uv - 0.5) - (0.5 - r); return length(max(q, 0.0)) - r; }
    void main() {
      // l'ombre portée : un rectangle très adouci, dessiné sous la carte
      if (uOmbre > 0.5) {
        float o = 1.0 - smoothstep(-0.06, 0.16, coin(vUv, 0.2));
        float a = o * o * 0.55 * uAlpha * (1.0 - smoothstep(0.0, 0.45, vBrume));   // l'ombre disparaît avant la carte dans la brume
        gl_FragColor = vec4(vec3(0.02, 0.015, 0.01) * a, a);
        return;
      }
      vec2 uv = vUv;
      float d = distance(uv, uSouris);
      uv += (uv - uSouris) * sin(d * 30.0 - uTemps * 5.0) * 0.014 * uSurvol * smoothstep(0.55, 0.0, d);
      vec3 papier = vec3(0.957, 0.941, 0.91);
      // packshot : posé sur le papier (le blanc du studio devient le papier) ; photo : couvre la carte
      vec2 e = uRatioImg > uRatioCarte ? vec2(1.0, uRatioCarte / uRatioImg) : vec2(uRatioImg / uRatioCarte, 1.0);
      vec2 couvre = uRatioImg > uRatioCarte ? vec2(uRatioCarte / uRatioImg, 1.0) : vec2(1.0, uRatioImg / uRatioCarte);
      vec2 iuv = uPackshot > 0.5 ? (uv - 0.5) / (e * 0.8) + 0.5 : (uv - 0.5) * couvre + 0.5;
      vec3 c = uPackshot > 0.5 ? papier : uFond;
      if (iuv.x >= 0.0 && iuv.x <= 1.0 && iuv.y >= 0.0 && iuv.y <= 1.0) {
        vec3 t = vec3(texture2D(uTex, iuv + vec2(uDecal, 0.0)).r, texture2D(uTex, iuv).g, texture2D(uTex, iuv - vec2(uDecal, 0.0)).b);
        c = uPackshot > 0.5 ? t / max(uFond, vec3(0.6)) * papier : t;   // le fond du studio, ramené au papier
      }
      c = mix(papier, c, uPret);
      c *= 0.985 + 0.03 * fract(sin(dot(floor(vUv * 420.0), vec2(12.9898, 78.233))) * 43758.5453);   // le grain du papier
      // filet or intérieur : la carte devient un tirage encadré
      float filet = 1.0 - smoothstep(0.0, 0.0035, abs(coin(vUv, 0.012) + 0.03));
      c = mix(c, vec3(0.79, 0.66, 0.42), filet * 0.55);
      // lumière rasante venue d'en haut à gauche, et un reflet qui glisse quand la carte tourne
      c *= 0.84 + 0.16 * smoothstep(1.3, 0.0, length(vUv - vec2(0.15, 0.05)));
      c += smoothstep(0.16, 0.0, abs(vUv.x + vUv.y * 0.45 - uLumiere)) * 0.07;
      c += 0.06 * uSurvol * smoothstep(0.5, 0.0, d);
      c = mix(c, uBrume, vBrume);                                       // profondeur
      float a = (1.0 - smoothstep(-0.003, 0.003, coin(vUv, 0.012))) * uAlpha;
      gl_FragColor = vec4(c * a, a);
    }`;

  function salle(racine) {
    const scene = racine.querySelector('.mm-j3d-scene');
    const canvas = racine.querySelector('.mm-j3d-canvas');
    const titre = racine.querySelector('[data-j3d-titre]');
    const legende = racine.querySelector('[data-j3d-legende]');
    let articles = [];
    try { articles = JSON.parse(racine.querySelector('[data-j3d-articles]').textContent); } catch (e) { articles = []; }
    const abandonner = (raison) => { console.warn('[Journal 3D] retour au Journal éditorial :', raison); html.classList.remove('mm-j3d-on'); racine.classList.add('mm-j3d-salle--repli'); };
    if (!html.classList.contains('mm-j3d-on') || !articles.length) return abandonner('aucun article ou 3D non demandée');
    const gl = contexte(canvas);
    if (!gl) return abandonner('WebGL indisponible');
    let prog, maillage;
    try { prog = programme(gl, VS_CARTE, FS_CARTE); maillage = plan(gl, 24); } catch (e) { return abandonner(e.message); }

    // Le titre s'écarte lettre par lettre : on découpe le texte, le h1 garde son texte complet.
    if (titre && !titre.dataset.j3dDecoupe) {
      titre.dataset.j3dDecoupe = '1';
      const noeud = [...titre.childNodes].find((n) => n.nodeType === 3);
      if (noeud) {
        const lettres = [...noeud.textContent];
        const f = document.createDocumentFragment();
        // ⚠️ chaque lettre est un inline-block : sans regroupement, la ligne se coupe AU MILIEU d'un mot
        //    (mesuré à 390 px : « Le Jo / urnal »). Les lettres d'un mot vivent dans un groupe insécable,
        //    et l'espace reste une vraie espace entre deux groupes.
        let mot = null;
        lettres.forEach((l, i) => {
          if (l === ' ') { f.append(' '); mot = null; return; }
          if (!mot) { mot = document.createElement('span'); mot.className = 'mm-j3d-motlettres'; f.append(mot); }
          const s = document.createElement('span'); s.className = 'mm-j3d-lettre'; s.style.setProperty('--i', String(i - (lettres.length - 1) / 2)); s.textContent = l; mot.append(s);
        });
        titre.setAttribute('aria-label', titre.textContent.trim());
        noeud.replaceWith(f);
      }
    }

    const mobile = () => innerWidth < 760;
    const ECART = 3.3;
    // les cartes restent dans le haut et le milieu de l'écran : le bas appartient à la légende
    const cartes = articles.map((a, i) => {
      const cote = i % 2 ? -1 : 1;
      return { a, i, cote, graine: i * 1.7, survol: 0, pret: 0, tex: texture(gl, null), ratioImg: a.ratio || 0.8, fond: [0.97, 0.96, 0.94], packshot: 1, decal: 0,
        y: 0.06 + 0.16 * Math.sin(i * 2.1), z: -i * ECART, mvp: null };
    });
    // Les images arrivent l'une après l'autre, la première d'abord : rien ne se bat pour la bande passante.
    (async () => {
      await (document.fonts && document.fonts.ready.catch(() => {}));
      for (const c of cartes) {
        try {
          let src;
          if (c.a.image) { const img = await charger(c.a.image); const f = fondDeLImage(img); c.fond = f.fond; c.packshot = f.packshot ? 1 : 0; c.ratioImg = img.naturalWidth / img.naturalHeight; src = img; }
          else { src = couvertureTexte(c.a.titre, c.a.date); c.ratioImg = 0.8; c.packshot = 0; c.fond = [0.96, 0.945, 0.913]; }
          gl.bindTexture(gl.TEXTURE_2D, c.tex);
          gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, src);
          c.charge = true;
        } catch (e) { c.charge = true; }
      }
    })();

    const etat = { visible: true, p: 0, pLisse: 0, camZ: 0, camZPrec: 0, courbe: 0, souris: [0, 0], sourisLisse: [0, 0], px: -1, py: -1, actif: -1, survol: -1, t0: performance.now(), dpr: 1, w: 1, h: 1 };
    // la galerie commence au loin : le titre a la scène pour lui, puis les cartes approchent
    const debut = () => (mobile() ? 12.5 : 9.5), fin = () => cartes[cartes.length - 1].z + 3.0;

    function dimensionner() {
      const r = scene.getBoundingClientRect();
      etat.dpr = Math.min(devicePixelRatio || 1, mobile() ? 1.5 : 1.75);
      etat.w = Math.max(1, r.width); etat.h = Math.max(1, r.height);
      canvas.width = Math.round(etat.w * etat.dpr); canvas.height = Math.round(etat.h * etat.dpr);
      gl.viewport(0, 0, canvas.width, canvas.height);
    }
    dimensionner();
    addEventListener('resize', () => { dimensionner(); demarrer(); }, { passive: true });

    // la salle ne calcule que lorsqu'on la voit, et jamais dans un onglet caché
    new IntersectionObserver((e) => { etat.visible = e[0].isIntersecting; if (etat.visible) demarrer(); }, { rootMargin: '100px' }).observe(racine);
    document.addEventListener('visibilitychange', () => { if (!document.hidden) demarrer(); });

    scene.addEventListener('pointermove', (e) => {
      const r = scene.getBoundingClientRect();
      etat.px = e.clientX - r.left; etat.py = e.clientY - r.top;
      etat.souris = [(etat.px / r.width) * 2 - 1, (etat.py / r.height) * 2 - 1];
    }, { passive: true });
    scene.addEventListener('pointerleave', () => { etat.px = etat.py = -1; etat.survol = -1; etiquette.cacher(); scene.style.cursor = ''; });
    // un toucher bref (pas un défilement) ouvre l'article touché
    let appui = null;
    scene.addEventListener('pointerdown', (e) => { appui = { x: e.clientX, y: e.clientY, t: performance.now() }; }, { passive: true });
    scene.addEventListener('pointerup', (e) => {
      if (!appui || Math.hypot(e.clientX - appui.x, e.clientY - appui.y) > 8 || performance.now() - appui.t > 450) return;
      if (e.target.closest('a, button')) return;
      const r = scene.getBoundingClientRect(), k = choisir(e.clientX - r.left, e.clientY - r.top);
      if (k) location.href = k.carte.a.url;
    });

    // une carte sous le point ? (projection des quatre coins, test dans le quadrilatère)
    function choisir(x, y) {
      let meilleur = null;
      for (const c of cartes) {
        if (!c.mvp || c.alpha < 0.35) continue;
        const pts = [[-0.5, -0.5], [0.5, -0.5], [0.5, 0.5], [-0.5, 0.5]].map(([u, v]) => {
          const [nx, ny, w] = M.projeter(c.mvp, u, v);
          return [(nx * 0.5 + 0.5) * etat.w, (1 - (ny * 0.5 + 0.5)) * etat.h, w];
        });
        if (pts.some((p) => p[2] <= 0)) continue;
        let dedans = true, signe = 0;
        for (let k = 0; k < 4; k++) {
          const [ax, ay] = pts[k], [bx, by] = pts[(k + 1) % 4];
          const cr = (bx - ax) * (y - ay) - (by - ay) * (x - ax);
          if (cr !== 0) { if (!signe) signe = Math.sign(cr); else if (Math.sign(cr) !== signe) { dedans = false; break; } }
        }
        if (!dedans) continue;
        // position du point dans la carte (approximation parallélogramme), pour centrer l'ondulation
        const [o, px, , py] = pts, ex = [px[0] - o[0], px[1] - o[1]], ey = [py[0] - o[0], py[1] - o[1]];
        const det = ex[0] * ey[1] - ex[1] * ey[0] || 1, dx = x - o[0], dy = y - o[1];
        const u = (dx * ey[1] - dy * ey[0]) / det, v = (ex[0] * dy - ex[1] * dx) / det;
        const z = pts[0][2];
        if (!meilleur || z < meilleur.z) meilleur = { carte: c, z, uv: [u, 1 - v] };
      }
      return meilleur;
    }

    let numero = racine.querySelector('[data-j3d-numero]'), date = racine.querySelector('[data-j3d-date]'), ltitre = racine.querySelector('[data-j3d-legende-titre]');
    function legender(c) {
      if (!legende) return;
      legende.classList.remove('is-change'); void legende.offsetWidth; legende.classList.add('is-change');
      numero.textContent = String(c.i + 1).padStart(2, '0');
      date.textContent = c.a.date ? ` — ${c.a.date}` : '';
      ltitre.textContent = c.a.titre;
    }

    let rafId = 0, derniere = performance.now();
    function demarrer() { if (!rafId && etat.visible && !document.hidden) { derniere = performance.now(); rafId = requestAnimationFrame(image); } }
    canvas.addEventListener('webglcontextlost', (e) => { e.preventDefault(); cancelAnimationFrame(rafId); rafId = 0; abandonner('contexte WebGL perdu'); });

    function image(maintenant) {
      rafId = 0;
      if (!etat.visible || document.hidden) return;
      const dt = Math.min(0.05, (maintenant - derniere) / 1000); derniere = maintenant;
      const t = (maintenant - etat.t0) / 1000;

      // ── le défilement : où en est-on dans la salle ?
      const r = racine.getBoundingClientRect();
      etat.p = clamp(-r.top / Math.max(1, r.height - innerHeight), 0, 1);
      etat.pLisse = lerp(etat.pLisse, etat.p, amorti(7, dt));
      const p = etat.pLisse;
      const voyage = lisse(0.04, 0.9, p), sortie = lisse(0.9, 1, p);
      racine.style.setProperty('--p', p.toFixed(4));
      racine.style.setProperty('--plongee', lisse(0, 0.16, p).toFixed(4));
      racine.style.setProperty('--sortie', sortie.toFixed(4));

      // ── la caméra
      etat.camZPrec = etat.camZ;
      const camZ = lerp(debut(), fin(), voyage);
      etat.camZ = camZ;
      const vitesse = (camZ - etat.camZPrec) / Math.max(dt, 1e-3);
      etat.courbe = lerp(etat.courbe, clamp(-vitesse * 0.035, -0.42, 0.42), amorti(6, dt));
      etat.sourisLisse[0] = lerp(etat.sourisLisse[0], etat.souris[0], amorti(3, dt));
      etat.sourisLisse[1] = lerp(etat.sourisLisse[1], etat.souris[1], amorti(3, dt));
      const mob = mobile();
      const camX = etat.sourisLisse[0] * 0.32 + Math.sin(t * 0.21) * 0.06, camY = -etat.sourisLisse[1] * 0.2 + Math.cos(t * 0.17) * 0.04;
      const vue = M.regarde([camX, camY, camZ], [camX * 0.35, camY * 0.35, camZ - 6]);
      const proj = M.perspective((mob ? 52 : 38) * Math.PI / 180, etat.w / etat.h, 0.1, 60);
      const pv = M.mul(proj, vue);

      // ── survol : la carte sous le curseur
      const sous = POINTEUR_FIN && etat.px >= 0 ? choisir(etat.px, etat.py) : null;
      if (sous) { etiquette.montrer(); scene.style.cursor = 'pointer'; } else if (etat.px >= 0) { etiquette.cacher(); scene.style.cursor = ''; }

      // ── la carte « en face » nourrit la légende
      let actif = null, ecart = Infinity;
      for (const c of cartes) { const d = camZ - c.z; if (d > 1.8 && Math.abs(d - 3.8) < ecart) { ecart = Math.abs(d - 3.8); actif = c; } }
      if (actif && actif.i !== etat.actif) { etat.actif = actif.i; legender(actif); }

      // ── rendu
      gl.clearColor(0, 0, 0, 0); gl.clear(gl.COLOR_BUFFER_BIT);
      gl.enable(gl.BLEND); gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA); gl.disable(gl.DEPTH_TEST);
      gl.useProgram(prog.p);
      gl.bindBuffer(gl.ARRAY_BUFFER, maillage.vb); gl.enableVertexAttribArray(prog.a); gl.vertexAttribPointer(prog.a, 2, gl.FLOAT, false, 0, 0);
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, maillage.ib);
      const u = prog.u;
      gl.uniformMatrix4fv(u.uProj, false, proj); gl.uniformMatrix4fv(u.uVue, false, vue);
      // la brume : l'encre de la salle, qui vire au papier à la sortie
      const brume = [lerp(0.085, 0.957, sortie), lerp(0.07, 0.941, sortie), lerp(0.06, 0.91, sortie)];
      gl.uniform3fv(u.uBrume, brume);
      gl.uniform1f(u.uTempsV, t); gl.uniform1f(u.uTemps, t % 62.832); // précision moyenne au fragment : on replie le temps
      const hauteur = mob ? 1.05 : 1.28, largeur = hauteur * 0.8, amplitude = mob ? 0.36 : 1.25;
      // de la plus lointaine à la plus proche : la transparence se compose dans le bon ordre
      for (let k = cartes.length - 1; k >= 0; k--) {
        const c = cartes[k], d = camZ - c.z;
        // une carte s'efface AVANT d'envahir l'écran, et les plus lointaines naissent de la brume
        c.alpha = (mob ? lisse(2.2, 3.8, d) : lisse(1.3, 2.9, d)) *   // sur un écran étroit, les cartes passent au centre : elles s'effacent plus tôt
          (1 - lisse(0, 0.45, sortie)) * lisse(-26, -16, -d);   // à la sortie, les cartes partent AVANT que le papier monte (sinon : un gris boueux)
        if (c.alpha <= 0.002) { c.mvp = null; continue; }
        const x = c.cote * (amplitude + 0.28 * Math.sin(c.graine)) + Math.sin(t * 0.4 + c.graine) * 0.05;
        const y = c.y + Math.cos(t * 0.5 + c.graine) * 0.05;
        const ry = -x * 0.2 + etat.sourisLisse[0] * 0.05, rx = 0.04 * Math.sin(c.graine) - etat.sourisLisse[1] * 0.04;
        const modele = M.modele(x, y, c.z, ry, rx, largeur * (1 + c.survol * 0.05), hauteur * (1 + c.survol * 0.05));
        c.mvp = M.mul(pv, modele);
        const vise = sous && sous.carte === c;
        c.survol = lerp(c.survol, vise ? 1 : 0, amorti(vise ? 9 : 5, dt));
        if (vise) c.souris = sous.uv;
        if (c.charge) c.pret = lerp(c.pret, 1, amorti(4, dt));
        c.decal = lerp(c.decal, clamp(Math.abs(vitesse) * 0.0009, 0, 0.009) + c.survol * 0.0025, amorti(8, dt));
        gl.uniformMatrix4fv(u.uModele, false, modele);
        gl.uniform1f(u.uCourbe, etat.courbe); gl.uniform1f(u.uGraine, c.graine); gl.uniform1f(u.uSurvol, c.survol); gl.uniform1f(u.uSurvolV, c.survol);
        gl.uniform1f(u.uPret, c.pret); gl.uniform1f(u.uDecal, c.decal); gl.uniform1f(u.uAlpha, c.alpha);
        gl.uniform1f(u.uRatioImg, c.ratioImg); gl.uniform1f(u.uRatioCarte, largeur / hauteur); gl.uniform1f(u.uPackshot, c.packshot);
        gl.uniform2fv(u.uSouris, c.souris || [0.5, 0.5]); gl.uniform3fv(u.uFond, c.fond);
        // le reflet glisse sur la carte quand la caméra la dépasse
        gl.uniform1f(u.uLumiere, 0.55 + (x - camX) * 0.22 + Math.sin(t * 0.3 + c.graine) * 0.12);
        gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, c.tex); gl.uniform1i(u.uTex, 0);
        // d'abord l'ombre portée, un peu plus bas et plus large, puis la carte
        gl.uniformMatrix4fv(u.uModele, false, M.modele(x + 0.07, y - 0.11, c.z - 0.08, ry, rx, largeur * 1.2, hauteur * 1.16));
        gl.uniform1f(u.uOmbre, 1);
        gl.drawElements(gl.TRIANGLES, maillage.n, gl.UNSIGNED_SHORT, 0);
        gl.uniformMatrix4fv(u.uModele, false, modele);
        gl.uniform1f(u.uOmbre, 0);
        gl.drawElements(gl.TRIANGLES, maillage.n, gl.UNSIGNED_SHORT, 0);
      }
      rafId = requestAnimationFrame(image);
    }
    racine.classList.add('mm-j3d-salle--prete');
    demarrer();
  }

  /* ════════════════════════════════════════════════════════════════════════
     ② LA COUVERTURE VIVANTE (article)
     ════════════════════════════════════════════════════════════════════════ */
  const VS_PLEIN = `attribute vec2 aPos; varying vec2 vUv; void main() { vUv = vec2(aPos.x + 0.5, 0.5 - aPos.y); gl_Position = vec4(aPos * 2.0, 0.0, 1.0); }`;
  const FS_COUVERTURE = `
    precision mediump float;
    uniform sampler2D uTex;
    uniform vec2 uRes, uSouris; uniform vec3 uFond;
    uniform float uRatioImg, uTemps, uVitesse, uOnde, uBalayage, uZoom;
    varying vec2 vUv;
    void main() {
      float ratio = uRes.x / uRes.y;
      vec2 uv = (vUv - 0.5) / uZoom + 0.5;
      uv.y += sin(uv.x * 3.14159) * uVitesse * 0.035;                   // le défilement plie l'image
      vec2 q = (uv - uSouris) * vec2(ratio, 1.0); float d = length(q);
      uv += normalize(q + 1e-5) / vec2(ratio, 1.0) * sin(d * 22.0 - uTemps * 4.0) * 0.016 * uOnde * smoothstep(0.45, 0.0, d);
      vec2 e = uRatioImg > ratio ? vec2(1.0, ratio / uRatioImg) : vec2(uRatioImg / ratio, 1.0);
      // le balayage : une bande qui traverse l'image et la déforme (un éclat de lumière sur un blanc ne se voit pas)
      float bande = smoothstep(0.16, 0.0, abs(vUv.x + vUv.y * 0.35 - uBalayage));
      uv.x += sin(vUv.y * 40.0) * 0.006 * bande;
      vec2 iuv = (uv - 0.5) / e + 0.5;
      vec3 c = uFond;
      if (iuv.x >= 0.0 && iuv.x <= 1.0 && iuv.y >= 0.0 && iuv.y <= 1.0) {
        float s = abs(uVitesse) * 0.004 + uOnde * 0.003 + bande * 0.006;
        c = vec3(texture2D(uTex, iuv + vec2(s, 0.0)).r, texture2D(uTex, iuv).g, texture2D(uTex, iuv - vec2(s, 0.0)).b);
      }
      c = mix(c, c * vec3(1.02, 0.98, 0.93), bande * 0.6);   // la bande se teinte d'or au passage
      gl_FragColor = vec4(c, 1.0);
    }`;

  function couverture(figure) {
    const img = figure.querySelector('img');
    if (!img || !html.classList.contains('mm-j3d-on')) return;
    const canvas = document.createElement('canvas');
    canvas.className = 'mm-j3d-couverture'; canvas.setAttribute('aria-hidden', 'true');
    const gl = contexte(canvas, true);
    if (!gl) return;
    let prog, quad;
    try { prog = programme(gl, VS_PLEIN, FS_COUVERTURE); quad = plan(gl, 1); } catch (e) { return; }
    const tex = texture(gl, null);
    const fondCss = getComputedStyle(img).backgroundColor.match(/[\d.]+/g) || [233, 227, 217];
    const fond = fondCss.slice(0, 3).map((v) => Number(v) / 255);
    const etat = { visible: true, souris: [0.5, 0.5], onde: 0, ondeCible: 0, vitesse: 0, yPrec: scrollY, t0: 0, w: 1, h: 1, pret: false };

    function placer() {
      const r = img.getBoundingClientRect(), f = figure.getBoundingClientRect();
      Object.assign(canvas.style, { top: `${r.top - f.top}px`, left: `${r.left - f.left}px`, width: `${r.width}px`, height: `${r.height}px` });
      const dpr = Math.min(devicePixelRatio || 1, 1.75);
      etat.w = r.width; etat.h = r.height;
      canvas.width = Math.round(r.width * dpr); canvas.height = Math.round(r.height * dpr);
      gl.viewport(0, 0, canvas.width, canvas.height);
    }
    const pret = img.complete ? Promise.resolve() : new Promise((ok) => img.addEventListener('load', ok, { once: true }));
    pret.then(() => img.decode ? img.decode().catch(() => {}) : null).then(() => {
      gl.bindTexture(gl.TEXTURE_2D, tex);
      try { gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img); } catch (e) { return; }
      figure.append(canvas); placer();
      // ⛔ redimensionner une toile l'EFFACE : mesuré, la couverture au repos devenait vide après un
      //    changement de taille de fenêtre (l'image HTML étant masquée). On redessine aussitôt.
      addEventListener('resize', () => { placer(); demarrer(); }, { passive: true });
      // contexte perdu (onglet en veille, pilote) : on rend la main à l'image HTML
      canvas.addEventListener('webglcontextlost', () => { cancelAnimationFrame(rafId); rafId = 0; figure.classList.remove('mm-j3d-couverture--vivante'); canvas.remove(); });
      etat.pret = true; etat.t0 = performance.now();
      // l'image HTML ne s'efface qu'une fois la toile dessinée : jamais de trou
      requestAnimationFrame(() => requestAnimationFrame(() => figure.classList.add('mm-j3d-couverture--vivante')));
      demarrer();
    });
    figure.addEventListener('pointermove', (e) => {
      const r = canvas.getBoundingClientRect();
      const nx = (e.clientX - r.left) / r.width, ny = (e.clientY - r.top) / r.height;
      const deplacement = Math.hypot(nx - etat.souris[0], ny - etat.souris[1]);
      etat.souris = [nx, ny]; etat.ondeCible = clamp(etat.ondeCible + deplacement * 9, 0, 1);
      figure.style.setProperty('--rx', `${(0.5 - ny) * 5}deg`); figure.style.setProperty('--ry', `${(nx - 0.5) * 7}deg`);
      demarrer();
    }, { passive: true });
    figure.addEventListener('pointerleave', () => { figure.style.setProperty('--rx', '0deg'); figure.style.setProperty('--ry', '0deg'); });
    new IntersectionObserver((e) => { etat.visible = e[0].isIntersecting; demarrer(); }).observe(figure);
    addEventListener('scroll', demarrer, { passive: true });

    let rafId = 0, derniere = 0;
    function demarrer() { if (!rafId && etat.pret && etat.visible) { derniere = performance.now(); rafId = requestAnimationFrame(image); } }
    function image(maintenant) {
      rafId = 0;
      const dt = Math.min(0.05, (maintenant - derniere) / 1000); derniere = maintenant;
      const t = (maintenant - etat.t0) / 1000;
      const dy = scrollY - etat.yPrec; etat.yPrec = scrollY;
      etat.vitesse = lerp(etat.vitesse, clamp(dy / Math.max(dt, 1e-3) / 2600, -1, 1), amorti(6, dt));
      etat.ondeCible = lerp(etat.ondeCible, 0, amorti(1.2, dt));
      etat.onde = lerp(etat.onde, etat.ondeCible, amorti(5, dt));
      const r = figure.getBoundingClientRect();
      const zoom = 1 + clamp(-r.top / Math.max(1, r.height), 0, 1) * 0.07;
      gl.useProgram(prog.p);
      gl.bindBuffer(gl.ARRAY_BUFFER, quad.vb); gl.enableVertexAttribArray(prog.a); gl.vertexAttribPointer(prog.a, 2, gl.FLOAT, false, 0, 0);
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, quad.ib);
      const u = prog.u;
      gl.uniform2f(u.uRes, etat.w, etat.h); gl.uniform2fv(u.uSouris, etat.souris); gl.uniform3fv(u.uFond, fond);
      gl.uniform1f(u.uRatioImg, img.naturalWidth / img.naturalHeight); gl.uniform1f(u.uTemps, t);
      gl.uniform1f(u.uVitesse, etat.vitesse); gl.uniform1f(u.uOnde, etat.onde); gl.uniform1f(u.uZoom, zoom);
      gl.uniform1f(u.uBalayage, lerp(-0.4, 1.8, easeOutExpo(clamp((t - 0.35) / 1.8, 0, 1))));
      gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, tex); gl.uniform1i(u.uTex, 0);
      gl.drawElements(gl.TRIANGLES, quad.n, gl.UNSIGNED_SHORT, 0);
      // on continue tant que quelque chose bouge ; sinon la toile se repose
      const bouge = t < 2.4 || Math.abs(etat.vitesse) > 0.002 || etat.onde > 0.002 || Math.abs(dy) > 0;
      if (bouge && etat.visible) rafId = requestAnimationFrame(image);
    }
  }

  /* ════════════════════════════════════════════════════════════════════════
     ③ LE MOUVEMENT ÉDITORIAL
     ════════════════════════════════════════════════════════════════════════ */
  // l'étiquette « Lire ↗ » qui suit le curseur (pointeur précis seulement)
  const etiquette = (() => {
    if (!POINTEUR_FIN) return { montrer() {}, cacher() {} };
    const el = document.createElement('div');
    el.className = 'mm-j3d-etiquette'; el.setAttribute('aria-hidden', 'true');
    const page = document.querySelector('[data-j3d-page]');
    el.innerHTML = `<span>${(page && page.dataset.j3dLire) || 'Lire'}</span><i>↗</i>`;
    document.body.append(el);
    let x = innerWidth / 2, y = innerHeight / 2, cx = x, cy = y, raf = 0, avant = 0;
    addEventListener('pointermove', (e) => { x = e.clientX; y = e.clientY; if (!raf) { avant = performance.now(); raf = requestAnimationFrame(suivre); } }, { passive: true });
    function suivre(m) {
      const dt = Math.min(0.05, (m - avant) / 1000); avant = m;
      cx = lerp(cx, x, amorti(14, dt)); cy = lerp(cy, y, amorti(14, dt));
      el.style.transform = `translate3d(${cx}px, ${cy}px, 0)`;
      raf = Math.hypot(x - cx, y - cy) > 0.3 ? requestAnimationFrame(suivre) : 0;
    }
    return { montrer() { el.classList.add('is-visible'); }, cacher() { el.classList.remove('is-visible'); } };
  })();

  // découpe d'un titre en mots, sans toucher aux éléments qu'il contient (flèches, liens)
  function decouperMots(el) {
    if (el.dataset.j3dMots) return;
    el.dataset.j3dMots = '1';
    let n = 0;
    const parcourir = (noeud) => {
      for (const enfant of [...noeud.childNodes]) {
        if (enfant.nodeType === 3) {
          const f = document.createDocumentFragment();
          enfant.textContent.split(/(\s+)/).forEach((m) => {
            if (!m) return;
            if (/^\s+$/.test(m)) { f.append(m); return; }
            const s = document.createElement('span'); s.className = 'mm-j3d-mot';
            const i = document.createElement('span'); i.textContent = m; i.style.setProperty('--i', String(n++));
            s.append(i); f.append(s);
          });
          enfant.replaceWith(f);
        } else if (enfant.nodeType === 1 && !enfant.matches('[aria-hidden="true"]')) parcourir(enfant);
      }
    };
    parcourir(el);
  }

  function reveler(racine) {
    const corps = racine.querySelector('[data-j3d-corps]');
    if (corps) {
      corps.querySelectorAll(':scope > h2').forEach((h) => { h.dataset.j3dRevele = 'titre'; });
      corps.querySelectorAll(':scope > figure, .article-produit').forEach((f) => { f.dataset.j3dRevele ||= f.matches('.article-produit') ? 'produit' : 'image'; });
      corps.querySelectorAll(':scope > blockquote').forEach((b) => { b.dataset.j3dRevele = 'citation'; });
    }
    const elements = [...racine.querySelectorAll('[data-j3d-revele]')];
    // ⛔ seuls les éléments SOUS la ligne de flottaison sont mis en attente : ce qui est déjà à l'écran
    //    ne disparaît jamais, et si ce fichier échoue rien n'est caché.
    const io = new IntersectionObserver((entrees) => {
      for (const e of entrees) if (e.isIntersecting) {
        const el = e.target; el.classList.add('is-vu'); io.unobserve(el);
        // une fois révélé, l'élément retrouve ses transitions d'origine (survol des cartes…)
        setTimeout(() => el.classList.remove('mm-j3d-attente', 'is-vu'), 2200);
      }
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });
    for (const el of elements) {
      if (el.getBoundingClientRect().top < innerHeight * 0.92) continue;
      if (['mots', 'titre'].includes(el.dataset.j3dRevele)) decouperMots(el);
      el.classList.add('mm-j3d-attente');
      io.observe(el);
    }
  }

  // cartes inclinables (pointeur précis) + étiquette « Lire »
  function incliner(racine) {
    racine.querySelectorAll('[data-j3d-carte]').forEach((carte) => {
      const surface = carte.querySelector('.mm-journal__card-image, .mm-journal__lead-image');
      if (!surface || !POINTEUR_FIN) return;
      surface.addEventListener('pointermove', (e) => {
        const r = surface.getBoundingClientRect(), nx = (e.clientX - r.left) / r.width, ny = (e.clientY - r.top) / r.height;
        surface.style.setProperty('--rx', `${(0.5 - ny) * 8}deg`); surface.style.setProperty('--ry', `${(nx - 0.5) * 10}deg`);
        surface.style.setProperty('--mx', `${nx * 100}%`); surface.style.setProperty('--my', `${ny * 100}%`);
      }, { passive: true });
      surface.addEventListener('pointerenter', () => { surface.classList.add('is-incline'); etiquette.montrer(); });
      surface.addEventListener('pointerleave', () => { surface.classList.remove('is-incline'); surface.style.setProperty('--rx', '0deg'); surface.style.setProperty('--ry', '0deg'); etiquette.cacher(); });
    });
  }

  // barre de lecture de l'article
  function progression() {
    const barre = document.querySelector('[data-j3d-progression] span'), corps = document.querySelector('[data-j3d-corps]');
    if (!barre || !corps) return;
    let attente = false;
    const maj = () => {
      const r = corps.getBoundingClientRect();
      barre.style.transform = `scaleX(${clamp((innerHeight * 0.3 - r.top) / Math.max(1, r.height - innerHeight * 0.4), 0, 1).toFixed(4)})`;
      attente = false;
    };
    addEventListener('scroll', () => { if (!attente) { attente = true; requestAnimationFrame(maj); } }, { passive: true });
    maj();
  }

  function monter(racine = document) {
    racine.querySelectorAll('[data-j3d-page]').forEach((page) => {
      if (page.dataset.j3dMonte) return;
      page.dataset.j3dMonte = '1';
      page.querySelectorAll('[data-j3d-salle]').forEach((s) => { try { salle(s); } catch (e) { html.classList.remove('mm-j3d-on'); } });
      page.querySelectorAll('[data-j3d-couverture]').forEach((f) => { try { couverture(f); } catch (e) {} });
      reveler(page); incliner(page);
    });
    progression();
  }
  monter();
  document.addEventListener('shopify:section:load', (e) => monter(e.target));
})();
